import { useCallback, useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { paintTheme, themeById } from './themes'
import type { Habit, Suggestion } from './api'
import {
  inbox, providers, triage, type Account, type Brand, type BrandedConnection, type Decision, type Mailbox,
  type MailboxConversation, type MailboxMessageSummary, type Rule, type TriageState,
} from './api'

export interface Conversation {
  key: string
  sourceId: string
  threadId: string
  subject: string
  /** The message the row speaks for: the newest one not sent by you. */
  lead: MailboxMessageSummary
  messages: MailboxMessageSummary[]
  messageCount: number
  unread: boolean
  lastMessageAt: string
  decision: Decision | null
  account: AccountInfo | null
  raw: MailboxConversation
}

export interface AccountInfo {
  id: string
  email: string
  name: string
  connectionId?: string
  brand: Brand
  /** 1-based, in the order accounts were connected; used for the 1–9 switcher keys. */
  number: number
  /** Colour index among accounts of the same provider (0 = the provider's own colour). */
  tone: number
  status: Account['status']
  sync: Account['sync']
}

export interface MailState {
  status: 'loading' | 'ready' | 'error'
  error?: string
  accounts: AccountInfo[]
  connections: BrandedConnection[]
  mailboxes: Mailbox[]
  conversations: Conversation[]
  hasMore: boolean
  triage: Omit<TriageState, 'items' | 'rules'>
  rules: Rule[]
  ownAddresses: Set<string>
  updatedAt: number
  /** Per sender: how much of their mail you open, reply to, ignore (last 60 days). */
  habits: Record<string, Habit>
  /** Rules Jev suggests from your behaviour, waiting for your OK. */
  suggestions: Suggestion[]
  /** Rules Jev learned and applied automatically (last two weeks). */
  learned: Suggestion[]
}

const EMPTY: MailState = {
  status: 'loading', accounts: [], connections: [], mailboxes: [], conversations: [], hasMore: false,
  triage: { enabled: false, pending: 0, classified: 0 }, rules: [], ownAddresses: new Set(), updatedAt: 0, habits: {}, suggestions: [], learned: [],
}

const PAGE = 100
let SNAPSHOT_KEY = 'mm.snapshot'

/** One live view of the signed-in person's mail, shared by every component. */
class MailStore {
  state: MailState = EMPTY
  private listeners = new Set<() => void>()
  private cursor: string | null = null
  private loaded = PAGE
  private decisions = new Map<string, Decision>()
  private timer = 0
  private stop: (() => void) | null = null
  private loading = false
  private again = false

  subscribe = (listener: () => void) => {
    this.listeners.add(listener)
    if (this.listeners.size === 1) this.start()
    return () => { this.listeners.delete(listener); if (!this.listeners.size) this.end() }
  }
  get = () => this.state
  private set(next: Partial<MailState>) { this.state = { ...this.state, ...next }; for (const listener of this.listeners) listener() }

  private lastPull = 0
  /** Ask the server to fetch new mail from every account in parallel, then refresh. */
  async pull() {
    if (Date.now() - this.lastPull < 10_000) return
    this.lastPull = Date.now()
    this.soon(0) // show what's already synced right away
    try { await fetch('/api/sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }) } catch {}
    this.soon(0)
  }
  /** Pull to refresh: sync every account now (no throttle) and wait for the new list. */
  async refreshNow() {
    this.lastPull = Date.now()
    try { await fetch('/api/sync', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: '{}' }) } catch {}
    await this.refresh()
  }
  private onVisible = () => { if (document.visibilityState === 'visible') void this.pull() }

  private start() {
    void this.refresh()
    document.addEventListener('visibilitychange', this.onVisible)
    window.addEventListener('focus', this.onVisible)
    // Events carry IDs, not mail; bursts during an import collapse into one refetch.
    this.stop = inbox.subscribe(() => this.soon(400), { onError: () => {} })
  }
  private end() {
    this.stop?.(); this.stop = null; clearTimeout(this.timer)
    document.removeEventListener('visibilitychange', this.onVisible)
    window.removeEventListener('focus', this.onVisible)
  }

  soon(ms = 300) {
    clearTimeout(this.timer)
    this.timer = window.setTimeout(() => { void this.refresh() }, ms)
  }

  /**
   * Conversations just marked Done or snoozed. They stay hidden until a refresh that
   * started after the server confirmed the change, so a refresh already in flight
   * (or one racing the write) can't bring them back for a moment.
   */
  private hidden = new Map<string, { at: number; settledAt: number | null }>()
  private ledger = new Set<string>()
  /** Tell the learner what you did with a message (open, reply, Done without opening…). */
  engage(kind: 'open' | 'open_other' | 'reply' | 'done' | 'done_unopened' | 'snooze' | 'not_important', message: { id: string; from: { email: string } }) {
    void fetch('/api/engage', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ kind, messageId: message.id, sender: message.from.email }) }).catch(() => {})
  }
  setSuggestions(suggestions: Suggestion[]) { this.set({ suggestions }) }
  /** Apply a rule on screen right away (the server already has it); refreshes confirm it. */
  addLocalRule(rule: Rule) { this.ruleEdits.set(rule.id, { rule, at: Date.now() }); this.set({ rules: [...this.state.rules.filter(r => r.id !== rule.id), rule] }) }
  removeLocalRule(id: string) { this.ruleEdits.set(id, { rule: null, at: Date.now() }); this.set({ rules: this.state.rules.filter(r => r.id !== id) }) }
  /** Conversations marked "not important" here, shown in Other straight away. */
  private filedAway = new Map<string, { at: number; label: string }>()
  fileAway(key: string, label: string) {
    this.filedAway.set(key, { at: Date.now(), label })
    this.set({ conversations: this.state.conversations.map(item => item.key === key ? this.toConversation(item.raw.messages.length ? item.raw : { ...item.raw, messages: item.messages }, this.state.accounts, this.state.ownAddresses) : item) })
  }
  unfile(key: string) { this.filedAway.delete(key); this.set({ conversations: this.state.conversations.map(item => item.key === key ? this.toConversation(item.raw.messages.length ? item.raw : { ...item.raw, messages: item.messages }, this.state.accounts, this.state.ownAddresses) : item) }) }
  /** Rules added or removed on this page. A refresh that started before the change can't undo it on screen. */
  private ruleEdits = new Map<string, { rule: Rule | null; at: number }>()
  private mergeRules(server: Rule[], startedAt: number): Rule[] {
    let rules = server
    for (const [id, edit] of this.ruleEdits) {
      if (startedAt > edit.at + 1_000 || Date.now() - edit.at > 60_000) { this.ruleEdits.delete(id); continue } // the server's answer is current
      rules = edit.rule ? (rules.some(r => r.id === id) ? rules : [...rules, edit.rule]) : rules.filter(r => r.id !== id)
    }
    return rules
  }
  /** Record (or, for Undo, forget) a conversation in the Done ledger. */
  async recordDone(item: Conversation, done: boolean) {
    const fps = item.messages.map(fingerprint)
    for (const fp of fps) done ? this.ledger.add(fp) : this.ledger.delete(fp)
    try { await fetch('/api/done', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ fps, done }) }) } catch { /* the SDK state still applies */ }
  }

  /** Important conversations older than the loaded pages (from /api/important). */
  private olderImportant: Conversation[] = []
  private fillingImportant = false
  private async fillImportant(mailboxIds: string[], infos: AccountInfo[], own: Set<string>) {
    if (this.fillingImportant) return
    this.fillingImportant = true
    try {
      const { keys } = await fetch('/api/important').then(r => r.json()) as { keys: Array<{ sourceId: string; threadId: string }> }
      const loaded = new Set(this.state.conversations.filter(item => !this.olderImportant.includes(item)).map(item => item.key))
      const missing = keys.filter(k => !loaded.has(`${k.sourceId}:${k.threadId}`)).slice(0, 100)
      const extra = missing.length ? (await inbox.mailboxConversations({ mailboxIds, keys: missing, limit: missing.length })).items.map(item => this.toConversation(item, infos, own)) : []
      const before = new Set(this.olderImportant.map(item => item.key)), after = new Set(extra.map(item => item.key))
      const changed = before.size !== after.size || [...after].some(key => !before.has(key))
      const old = new Set(this.olderImportant)
      this.olderImportant = extra
      if (changed) {
        const base = this.state.conversations.filter(item => !old.has(item))
        const have = new Set(base.map(item => item.key))
        this.set({ conversations: this.visible([...base, ...extra.filter(item => !have.has(item.key))]) })
      }
    } catch { /* the loaded pages still show */ } finally { this.fillingImportant = false }
  }
  /** Drop what you just marked Done/snoozed and what the Done ledger covers. */
  private visible(conversations: Conversation[]) {
    let out = conversations
    if (this.hidden.size) out = out.filter(item => !this.hidden.has(item.key))
    if (this.ledger.size) out = out.filter(item => !(item.messages.length && item.messages.every(m => this.ledger.has(fingerprint(m)))))
    return out
  }

  // ---- Instant start: the last inbox, painted before the network answers ----
  private saveTimer = 0
  private snapshot() {
    clearTimeout(this.saveTimer)
    this.saveTimer = window.setTimeout(() => {
      try {
        const st = this.state
        const conversations = st.conversations.slice(0, 120).map(item => ({ ...item, raw: { ...item.raw, messages: [] } }))
        localStorage.setItem(SNAPSHOT_KEY, JSON.stringify({ v: 1, at: Date.now(), accounts: st.accounts, connections: st.connections, mailboxes: st.mailboxes, conversations, rules: st.rules, habits: st.habits, ownAddresses: [...st.ownAddresses], triage: st.triage }))
      } catch { try { localStorage.removeItem(SNAPSHOT_KEY) } catch {} }
    }, 1_500)
  }
  /** Load this user's last inbox (once, before the first render). */
  hydrate(userId: string) {
    if (this.state.status !== 'loading') return
    SNAPSHOT_KEY = `mm.snapshot.${userId}`
    try {
      const raw = localStorage.getItem(SNAPSHOT_KEY)
      if (!raw) return
      const snap = JSON.parse(raw)
      if (snap.v !== 1 || Date.now() - snap.at > 7 * 86_400_000) return
      this.state = { ...this.state, status: 'ready', accounts: snap.accounts, connections: snap.connections, mailboxes: snap.mailboxes, conversations: snap.conversations, rules: snap.rules, habits: snap.habits ?? {}, ownAddresses: new Set(snap.ownAddresses), triage: snap.triage, updatedAt: snap.at, hasMore: true }
    } catch { /* start empty */ }
  }

  async refresh() {
    if (this.loading) { this.again = true; return }
    this.loading = true
    const startedAt = Date.now()
    try {
      const [mailboxes, accounts, { connections }, triageState] = await Promise.all([
        inbox.mailboxes(), inbox.accounts(), providers(), triage().catch(() => null),
      ])
      if (triageState) this.decisions = new Map(triageState.items.map(item => [item.id, item]))
      if (triageState?.done) this.ledger = new Set(triageState.done)
      const active = mailboxes.filter(mailbox => mailbox.status === 'active')
      const ordered = [...connections].sort((a, b) => a.createdAt.localeCompare(b.createdAt))
      const infos: AccountInfo[] = accounts
        .filter(account => account.status !== 'disconnected')
        .map(account => {
          const connection = ordered.find(item => item.id === account.connectionId || item.sourceIds.includes(account.id))
          return {
            id: account.id, email: account.email, name: account.name, connectionId: connection?.id,
            brand: connection?.brand ?? 'imap', number: 0, status: account.status, sync: account.sync,
          }
        })
        .sort((a, b) => ordered.findIndex(c => c.id === a.connectionId) - ordered.findIndex(c => c.id === b.connectionId))
        .map((info, index, all) => ({ ...info, number: index + 1, tone: all.slice(0, index).filter(other => other.brand === info.brand).length }))
      const ownAddresses = new Set(infos.map(info => info.email.toLowerCase()))

      let conversations: Conversation[] = []
      let hasMore = false
      if (active.length) {
        const mailboxIds = active.slice(0, 50).map(mailbox => mailbox.id)
        const items: MailboxConversation[] = []
        let cursor: string | undefined
        do {
          const page = await inbox.mailboxConversations({ mailboxIds, limit: PAGE, ...(cursor ? { cursor } : {}), query: { done: false, snoozed: false, folder: 'inbox' } })
          items.push(...page.items)
          cursor = page.nextCursor ?? undefined
        } while (cursor && items.length < this.loaded)
        this.cursor = cursor ?? null
        hasMore = !!cursor
        conversations = items.map(item => this.toConversation(item, infos, ownAddresses))
        // Important mail older than the loaded pages: shown from the last lookup now, refreshed in the background.
        const have = new Set(conversations.map(item => item.key))
        conversations = [...conversations, ...this.olderImportant.filter(item => !have.has(item.key))]
        void this.fillImportant(mailboxIds, infos, ownAddresses)
      }
      for (const [key, entry] of this.hidden) {
        // Confirmed before this refresh began: the server's answer is now the truth (new mail may revive it).
        if ((entry.settledAt !== null && startedAt > entry.settledAt) || Date.now() - entry.at > 60_000) this.hidden.delete(key)
      }
      // The Done ledger: if every message in a conversation was marked Done, keep it out even if the mailbox state was lost.
      conversations = this.visible(conversations)
      this.set({
        status: 'ready', error: undefined, accounts: infos, connections, mailboxes: active, conversations, hasMore, ownAddresses,
        rules: triageState?.rules ? this.mergeRules(triageState.rules, startedAt) : this.state.rules, updatedAt: Date.now(),
        habits: triageState?.habits ?? this.state.habits, suggestions: triageState?.suggestions ?? this.state.suggestions, learned: triageState?.learned ?? this.state.learned,
        triage: triageState ? { enabled: triageState.enabled, pending: triageState.pending, classified: triageState.classified } : this.state.triage,
      })
      this.snapshot()
      // While an account imports or mail is still being read, look again soon.
      const importing = infos.some(info => info.sync.coverage === 'empty' || !info.sync.lastSyncAt)
      // New mail that Jev hasn't read yet sits in Other; keep looking so it's promoted as soon as it's classified.
      const fresh = Date.now() - 10 * 60_000
      const unsorted = !!triageState?.enabled && conversations.some(item => !item.decision && Date.parse(item.lastMessageAt) > fresh && !ownAddresses.has(item.lead.from.email.toLowerCase()))
      if (importing || unsorted || (triageState?.pending ?? 0) > 0) this.soon(2_500)
    } catch (error) {
      this.set({ status: this.state.status === 'ready' ? 'ready' : 'error', error: error instanceof Error ? error.message : 'Couldn’t load your mail.' })
      this.soon(5_000)
    } finally {
      this.loading = false
      if (this.again) { this.again = false; this.soon(50) }
    }
  }

  loadMore() {
    if (!this.cursor) return
    this.loaded += PAGE
    void this.refresh()
  }

  /** One conversation by key, for a reload straight into a thread that isn't in the loaded inbox page. */
  async fetchConversation(key: string): Promise<Conversation | null> {
    const split = key.indexOf(':')
    if (split < 1 || !this.state.mailboxes.length) return null
    const sourceId = key.slice(0, split), threadId = key.slice(split + 1)
    try {
      const page = await inbox.mailboxConversations({ mailboxIds: this.state.mailboxes.slice(0, 50).map(m => m.id), keys: [{ sourceId, threadId }] })
      const item = page.items[0]
      return item ? this.toConversation(item, this.state.accounts, this.state.ownAddresses) : null
    } catch { return null }
  }

  /** Turn an SDK conversation into the app's shape (used by search). */
  wrap(item: MailboxConversation): Conversation { return this.toConversation(item, this.state.accounts, this.state.ownAddresses) }

  private toConversation(item: MailboxConversation, accounts: AccountInfo[], own: Set<string>): Conversation {
    const messages = [...item.messages].sort((a, b) => a.receivedAt.localeCompare(b.receivedAt))
    const lead = [...messages].reverse().find(message => !own.has(message.from.email.toLowerCase())) ?? messages[messages.length - 1]!
    const found = [...messages].reverse().map(message => this.decisions.get(message.id)).find(Boolean) ?? null
    // "Not important" on this conversation: it's in Other now, whatever an older answer says.
    const filed = this.filedAway.get(`${item.sourceId}:${item.threadId}`)
    const decision = filed && Date.now() - filed.at < 120_000 && (!found || found.important)
      ? ({ ...(found ?? { id: messages.at(-1)!.id, category: 'notification', sender: 'company', urgency: 0, importance: 0, priority: 0 }), important: false, needsReply: false, critical: false, reason: '', label: filed.label } as Decision)
      : found
    return {
      key: `${item.sourceId}:${item.threadId}`,
      sourceId: item.sourceId,
      threadId: item.threadId,
      subject: item.subject || lead?.subject || '(no subject)',
      lead,
      messages,
      messageCount: item.messageCount,
      unread: !item.isRead,
      lastMessageAt: item.lastMessageAt,
      decision,
      account: accounts.find(account => account.id === item.sourceId) ?? null,
      raw: item,
    }
  }

  /** Optimistically hide a conversation (Done / Snooze) until the next refresh confirms it. */
  /** Take a conversation off screen right away (Done, Snooze); call settle() once the server has it. */
  hide(key: string) { if (!this.hidden.has(key)) this.hidden.set(key, { at: Date.now(), settledAt: null }) }
  remove(key: string) {
    this.hide(key)
    this.set({ conversations: this.state.conversations.filter(item => item.key !== key) })
  }
  /** The server confirmed the change (or it failed / was undone, with keep=false): let refreshes decide again. */
  settle(key: string, keepHidden = true) {
    const entry = this.hidden.get(key)
    if (!entry) return
    if (keepHidden) entry.settledAt = Date.now()
    else this.hidden.delete(key)
    this.soon(50)
  }
  markRead(key: string) {
    this.set({ conversations: this.state.conversations.map(item => item.key === key ? { ...item, unread: false } : item) })
  }
}

export const store = new MailStore()
export const useMail = () => useSyncExternalStore(store.subscribe, store.get)

/** The Important / Other split and the order within each. */
/** The newest exact sender or domain rule matching an address. */
export function senderRule(rules: Rule[], email: string): Rule | undefined {
  const address = email.toLowerCase()
  const domain = address.split('@')[1] ?? ''
  return [...rules].reverse().find(rule =>
    (rule.kind === 'sender' && rule.pattern === address) ||
    (rule.kind === 'domain' && (domain === rule.pattern || domain.endsWith(`.${rule.pattern}`))))
}

export function partition(conversations: Conversation[], triageOn: boolean, ownAddresses: Set<string> = new Set(), rules: Rule[] = [], habits: Record<string, Habit> = {}) {
  const needsReply: Conversation[] = []
  const worth: Conversation[] = []
  const sorting: Conversation[] = []
  const other = new Map<string, Conversation[]>()
  for (const item of conversations) {
    const exact = senderRule(rules, item.lead.from.email)
    const ageRule = rules.find(rule => rule.kind === 'age')
    if (!(exact?.action === 'important') && ageRule && Date.now() - Date.parse(item.lastMessageAt) > Number(ageRule.pattern) * 86_400_000) {
      const list = other.get('Older') ?? []
      list.push(item); other.set('Older', list)
      continue
    }
    if (exact) {
      if (exact.action === 'important') { (item.decision?.needsReply ? needsReply : worth).push(item); continue }
      const list = other.get(exact.label) ?? []
      list.push(item); other.set(exact.label, list)
      continue
    }
    const d = item.decision
    if (!d && ownAddresses.has(item.lead.from.email.toLowerCase())) {
      const list = other.get('Sent by you') ?? []
      list.push(item); other.set('Sent by you', list)
      continue
    }
    if (!d) {
      // Not classified yet: it waits in Other and only moves to Important once Jev says it matters.
      if (triageOn) { sorting.push(item); const list = other.get('Sorting') ?? []; list.push(item); other.set('Sorting', list) }
      else worth.push(item)
      continue
    }
    if (d.important && d.needsReply) needsReply.push(item)
    else if (d.important) worth.push(item)
    else {
      const list = other.get(d.label) ?? []
      list.push(item)
      other.set(d.label, list)
    }
  }
  // People you actually reply to rise a little within Needs reply.
  const habitBoost = (item: Conversation) => { const h = habits[item.lead.from.email.toLowerCase()]; return h ? Math.min(0.15, h.replied * 0.05 + (h.opened >= 3 ? 0.05 : 0)) : 0 }
  needsReply.sort((a, b) => (b.decision!.priority + habitBoost(b) - a.decision!.priority - habitBoost(a)) || b.lastMessageAt.localeCompare(a.lastMessageAt))
  // Within each Other group, what matters most comes first; ties go to the newest.
  const rank = (item: Conversation) => item.decision ? item.decision.importance * 0.6 + item.decision.urgency * 0.4 : 0
  for (const list of other.values()) list.sort((a, b) => (rank(b) - rank(a)) || b.lastMessageAt.localeCompare(a.lastMessageAt))
  const groups = [...other.entries()].sort((a, b) => b[1].length - a[1].length)
  return { needsReply, worth, sorting, other: groups, importantCount: needsReply.length + worth.length, otherCount: groups.reduce((n, [, list]) => n + list.length, 0) }
}

// ---- Recently opened, kept in this browser ----
const RECENT_KEY = 'mm.recent'
export interface RecentOpen { key: string; sender: string; subject: string; at: number; email: string; brand?: Brand }
export function recentOpens(): RecentOpen[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]') } catch { return [] }
}
export function rememberOpen(item: Conversation) {
  const entry: RecentOpen = { key: item.key, sender: item.lead.from.name || item.lead.from.email, email: item.lead.from.email, subject: item.subject, at: Date.now(), brand: item.account?.brand }
  localStorage.setItem(RECENT_KEY, JSON.stringify([entry, ...recentOpens().filter(open => open.key !== item.key)].slice(0, 12)))
}

/** Same fingerprint as server/src/ledger.ts: sender | subject | received minute. */
export const fingerprint = (m: { from: { email: string }; subject: string; receivedAt: string }) =>
  `${m.from.email.trim().toLowerCase()}|${m.subject.trim()}|${new Date(m.receivedAt).toISOString().slice(0, 16)}`

// ---- Preferences ----
export interface Prefs { /** 'system', or a theme id (built-in or custom). */ theme: string; density: 'compact' | 'comfortable'; sidebar: boolean }
const PREFS_KEY = 'mm.prefs'
const DEFAULT_PREFS: Prefs = { theme: 'dark', density: 'compact', sidebar: true }
const prefListeners = new Set<() => void>()
let prefs: Prefs = (() => { try { return { ...DEFAULT_PREFS, ...JSON.parse(localStorage.getItem(PREFS_KEY) || '{}') } } catch { return DEFAULT_PREFS } })()
export function setPrefs(next: Partial<Prefs>) {
  const densityChange = next.density !== undefined && next.density !== prefs.density
  prefs = { ...prefs, ...next }
  localStorage.setItem(PREFS_KEY, JSON.stringify(prefs))
  if (next.theme !== undefined) {
    // Ease colours across a theme switch.
    document.documentElement.classList.add('theme-anim')
    clearTimeout(themeTimer); themeTimer = window.setTimeout(() => document.documentElement.classList.remove('theme-anim'), 260)
  }
  if (densityChange) animateDensity(() => { applyTheme(); for (const listener of prefListeners) listener() })
  else { applyTheme(); for (const listener of prefListeners) listener() }
}

let themeTimer = 0
const DENSITY_MS = 340
let densityTimer = 0
/**
 * Switch density with every size easing together (rows, type, avatars, bars),
 * while the selected row stays where your eyes are instead of jumping.
 */
function animateDensity(apply: () => void) {
  const root = document.documentElement
  const scroller = document.querySelector<HTMLElement>('.main')
  const anchor = document.querySelector<HTMLElement>('.row.selected') ?? document.querySelector<HTMLElement>('.row')
  const before = anchor && scroller ? anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top : null
  root.classList.add('density-anim')
  apply()
  clearTimeout(densityTimer)
  if (anchor && scroller && before !== null) {
    const start = performance.now()
    const hold = () => {
      const drift = anchor.getBoundingClientRect().top - scroller.getBoundingClientRect().top - before
      scroller.scrollTop += drift
      if (performance.now() - start < DENSITY_MS + 60) requestAnimationFrame(hold)
    }
    requestAnimationFrame(hold)
  }
  densityTimer = window.setTimeout(() => root.classList.remove('density-anim'), DENSITY_MS + 80)
}
export const usePrefs = () => useSyncExternalStore(listener => { prefListeners.add(listener); return () => prefListeners.delete(listener) }, () => prefs)
export function applyTheme() {
  const systemDark = matchMedia('(prefers-color-scheme: dark)').matches
  const theme = prefs.theme === 'system' ? themeById(systemDark ? 'dark' : 'light') : themeById(prefs.theme) ?? themeById('dark')
  paintTheme(theme, systemDark ? 'dark' : 'light')
  document.documentElement.dataset.density = prefs.density
}

/** Run an async action once, exposing whether it's running and its last error. */
export function useAction<A extends unknown[]>(action: (...args: A) => Promise<unknown>) {
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)
  const mounted = useRef(true)
  useEffect(() => () => { mounted.current = false }, [])
  const run = useCallback(async (...args: A) => {
    setBusy(true); setError(null)
    try { return await action(...args) } catch (failure) { if (mounted.current) setError(failure); return undefined } finally { if (mounted.current) setBusy(false) }
  }, [action])
  return { run, busy, error, setError }
}
